import React from "react";
import { useDispatch, useSelector } from "react-redux";
import MultiSelect from "../Selectors/MultiSelect";
import GroupedMultiSelect from "../Selectors/GroupedMultiSelect";
import { loadStores } from "../../../api_calls/campaigns";
import { checkUserRole } from "../../../api_calls/userManagement";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { Badge, Box, IconButton, Stack } from "@mui/material";
import { TextButtonStyle } from "../Buttons/Styles";
import StoreSelectionModal from "./StoreSelectionModal";
import { CheckCircle, Search, SearchOutlined, Store } from "@mui/icons-material";
import { HtmlTooltip } from "../StyledComponents/styled";
import { DetailedSelectionIconWithTooltip } from "../../../api_calls/utils";
import {
    ROOT_RETAILER_GROUP_PARAM,
    resolveLoggedInUserType,
    shouldUseRetailerStoreHierarchy,
    toStoreOptionsWithRootRetailer,
} from "../Filters/storeFilterUtils";

const DetailedStoreFilter = ({ reduxKeywordToSaveValue }) => {
    // Redux state selection
    const storeDetails = useSelector((state) => state.userSelections);

    // Local state management
    const [defaultValuesLoaded, setDefaultValuesLoaded] = React.useState(false);
    const [changesMade, setChangesMade] = React.useState(false);
    const [showSelectionModal, setShowSelectionModal] = React.useState(false);
    const [storeNameOptionSelected, setStoreNameOptionsSelected] = React.useState([]);
    const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);

    // Redux dispatch action
    const dispatchAction = useDispatch();
    const loggedInUserType = resolveLoggedInUserType();
    const isBrandUser =
        storeDetails?.brand_report_view === true || checkUserRole()?.user_role === "Brand";

    // Extract and sort store names from store list
    const storeList = storeDetails?.stores_list
        ? [...storeDetails.stores_list.map((storeInfo) => {
            return storeInfo.name;
        })].sort()
        : [];

    // Same store names as the flat list, plus root retailer when the active-store record has one.
    const storeOptions = toStoreOptionsWithRootRetailer(storeList, storeDetails?.stores_list || []);
    const showRetailerHierarchy = shouldUseRetailerStoreHierarchy({
        isBrand: isBrandUser,
        retailerHierarchyAvailable,
        storeOptions,
    });

    /**
     * Fetches store IDs based on provided store names
     * @param {string|string[]} storeNames - Single store name or array of store names
     * @returns {number[]} Array of store IDs
     */
    const fetchStoreIds = (storeNames) => {
        // Ensure storeNames is always an array
        const reformStoreNames = Array.isArray(storeNames) ? storeNames : [storeNames];

        // Filter stores from the list and map to their IDs
        let storeIds =
            typeof storeDetails.stores_list !== "undefined" && storeDetails.stores_list.length !== 0 && reformStoreNames.length !== 0
                ? storeDetails.stores_list.filter(storeListOptions => reformStoreNames.includes(storeListOptions.name)).map(store => store.id)
                : [];

        return storeIds;
    }

    // Load stores on component mount
    React.useEffect(() => {
        loadStores().then(() => {
            setDefaultValuesLoaded(true);
        })
    }, []);

    // Same access check as the dashboard store filter. One retailer, or a brand user, stays flat.
    React.useEffect(() => {
        let shouldApplyResult = true;

        if (loggedInUserType === "") {
            setRetailerHierarchyAvailable(false);
            return undefined;
        }

        loadRetailerAccessAvailability(loggedInUserType)
            .then((retailerAccessInfo) => {
                if (shouldApplyResult) {
                    setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
                }
            })
            .catch(() => {
                if (shouldApplyResult) {
                    setRetailerHierarchyAvailable(false);
                }
            });

        return () => {
            shouldApplyResult = false;
        };
    }, [loggedInUserType]);

    // Sync selected store names with Redux state
    React.useEffect(() => {
        if (storeDetails?.[reduxKeywordToSaveValue]?.selected_store_names) {
            setStoreNameOptionsSelected(storeDetails[reduxKeywordToSaveValue].selected_store_names);
        }
    }, [storeDetails, reduxKeywordToSaveValue]);

    return defaultValuesLoaded ? (
        <>
            <Box width={"100%"}>
                {showRetailerHierarchy === true ? (
                    <GroupedMultiSelect
                        enableFullWidth={true}
                        label={"Store"}
                        options={storeOptions}
                        defaultVal={storeNameOptionSelected}
                        groupParam={ROOT_RETAILER_GROUP_PARAM}
                        showGroupNames={true}
                        showSelectionSummary={true}
                        usedFor="storeFilter"
                        selectionCheck={(option, value) => option.label === value.label}
                        onChangeCall={(selectedOption) => {
                            setChangesMade(true);
                            setStoreNameOptionsSelected(selectedOption);
                        }}
                        onBlurCall={() => {
                            if (changesMade === true) {
                                const storeNames = storeNameOptionSelected.length !== 0 ? storeNameOptionSelected.map(store => store.label) : [];
                                const StoreIds = storeNameOptionSelected.length !== 0 ? fetchStoreIds(storeNames) : [];

                                dispatchAction(
                                    updateStateData({
                                        [reduxKeywordToSaveValue]: {
                                            ...storeDetails[reduxKeywordToSaveValue],
                                            "store_ids": StoreIds,
                                            "selected_store_names": storeNameOptionSelected
                                        },
                                    })
                                );
                                setChangesMade(false);
                            }
                        }}
                        onClearCall={() => {
                            setStoreNameOptionsSelected([]);
                            dispatchAction(
                                updateStateData({
                                    [reduxKeywordToSaveValue]: {
                                        ...storeDetails[reduxKeywordToSaveValue],
                                        "store_ids": [],
                                        "selected_store_names": []
                                    },
                                })
                            );
                        }}
                        additionalIconWithTooltip={true}
                        additionalIconMarkup={(setOpen) => {
                            return DetailedSelectionIconWithTooltip((e) => {
                                e.stopPropagation();
                                setOpen(false);
                                setShowSelectionModal(true);
                            })
                        }}
                    />
                ) : (
                <MultiSelect
                    enableFullWidth={true}
                    label={"Store"}
                    options={[...storeList].map((storeName) => {
                        return { value: storeName, label: storeName };
                    })}
                    defaultVal={storeNameOptionSelected}
                    selectionCheck={(option, value) => option.label === value.label}
                    usedFor="storeFilter"
                    onChangeCall={(selectedOption) => {
                        setChangesMade(true);
                        setStoreNameOptionsSelected(selectedOption);
                    }}
                    onBlurCall={(e) => {
                        if (changesMade === true) {
                            const storeNames = storeNameOptionSelected.length !== 0 ? storeNameOptionSelected.map(store => store.label) : [];
                            const StoreIds = storeNameOptionSelected.length !== 0 ? fetchStoreIds(storeNames) : [];

                            dispatchAction(
                                updateStateData({
                                    [reduxKeywordToSaveValue]: {
                                        ...storeDetails[reduxKeywordToSaveValue],
                                        "store_ids": StoreIds,
                                        "selected_store_names": storeNameOptionSelected
                                    },
                                })
                            );

                            // filterSingleValue("id-campaign", StoreIds, vizId);
                            setChangesMade(false);
                        }
                    }}
                    onClearCall={() => {
                        setStoreNameOptionsSelected([]);
                        dispatchAction(
                            updateStateData({
                                [reduxKeywordToSaveValue]: {
                                    ...storeDetails[reduxKeywordToSaveValue],
                                    "store_ids": [],
                                    "selected_store_names": []
                                },
                            })
                        );
                    }}
                    additionalIconWithTooltip={true}
                    additionalIconMarkup={(setOpen) => {
                        return DetailedSelectionIconWithTooltip((e) => {
                            e.stopPropagation();
                            setOpen(false);
                            setShowSelectionModal(true);
                        })
                    }
                    }
                    allowDuplicates={true}
                />
                )}
            </Box>
            <Box height={2} />
            {/* <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 0.5,
                }}
            >
                <TextButtonStyle
                    sx={{
                        px: 0.1,
                        fontWeight: 400,
                    }}
                    onClick={(e) => {
                        setShowSelectionModal(true);
                    }}> Detailed Selection</TextButtonStyle>
            </Box> */}
            {
                showSelectionModal && (
                    <StoreSelectionModal
                        open={showSelectionModal}
                        onClose={() => {
                            setShowSelectionModal(false);
                            dispatchAction(deleteItems(["store_selection_pagination", "store_list_filter_applied", "detailed_stores_filtered"]));
                        }}
                        storeSelectionReduxKey={reduxKeywordToSaveValue}
                    />
                )
            }
        </>
    ) : (
        <></>
    );
};

export default DetailedStoreFilter;
